/**
 * Fase 1 / C18 — aba Documentos do Cliente (decisões C18.1–C18.6 de 09/10/2026):
 * nome livre + tipo obrigatório; até 20 MB (PDF, imagens, Office e e-mail); substituir = nova versão
 * (as anteriores ficam no histórico e continuam disponíveis); sem exclusão: inativação com justificativa,
 * auditada. Permissões próprias: ver, enviar e inativar (🔒).
 * Pacote 2 · P2-33: o MESMO componente serve à aba Documentos da Oportunidade (D5), com "Relacionado a" opcional
 * (versão de proposta da própria oportunidade) e as permissões de documento_oportunidade (D6).
 */
import { Fragment, useState } from 'react';
import { api } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { Ajuda, EstadoCarga, Modal, SeloSituacao, Th, useCarga, useOrdenacao } from './ui.tsx';
import { arquivoParaApi } from './Pedidos.tsx';
import { BarraFiltros } from './filtros.tsx';
import { usePersonalizacao } from './personalizacao.tsx';
import { Icone } from '../componentes/icones.tsx';

type TipoDocumento = string;
interface VersaoDocumento {
  id: string;
  numero: number;
  nomeArquivo: string;
  tamanho: number;
  tipoArquivo: string;
  observacao: string;
  enviadoPor: string;
  enviadoEm: string;
}
interface DocumentoCliente {
  id: string;
  tipo: TipoDocumento;
  tipoRotulo: string;
  nome: string;
  situacao: 'ativo' | 'inativo';
  criadoEm: string;
  criadoPor: string;
  inativadoEm: string | null;
  inativadoPor: string | null;
  motivoInativacao: string | null;
  atualizadoEm: string;
  versoes: VersaoDocumento[];
  /** Oportunidade: proposta/versão relacionada (opcional) */
  relacionado?: { id: string; rotulo: string } | null;
  /** P3-42: documento restrito; "oculto" quando o usuário não tem a permissão própria (nome e arquivo não vêm) */
  restrito?: boolean;
  oculto?: boolean;
}
interface RespostaDocumentos {
  tipos: { id: TipoDocumento; rotulo: string }[];
  limiteMb: number;
  armazenamentoConfigurado: boolean;
  documentos: DocumentoCliente[];
  /** Oportunidade: versões de proposta disponíveis para "Relacionado a" */
  relacionados?: { id: string; rotulo: string }[];
  /** P3-42: o usuário pode ver/enviar documentos restritos */
  podeRestrito?: boolean;
}
/** Onde os documentos vivem: Cliente (C18) ou Oportunidade (P2-33). */
export interface OrigemDocumentos {
  base: string;
  entidade: 'documento_cliente' | 'documento_oportunidade';
  /** P2-03/P2-04 (D7) */
  lista: 'cliente-documentos' | 'oportunidade-documentos';
  titulo: string;
  vazio: string;
  tituloEnvio: string;
  teste: string;
  /** novos documentos permitidos (Cliente ativo; na Oportunidade, sempre — D5) */
  podeCriar: boolean;
  motivoBloqueio?: string;
  /** avisa a tela que contém a aba (ex.: a Ficha recarrega a Timeline) */
  aoMudar?: () => void;
}

export const ACEITE_DOCUMENTO = '.pdf,.jpg,.jpeg,.png,.docx,.xlsx,.pptx,.eml,.msg';
const tamanhoTexto = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const dataHoraBr = (v: string) => new Date(v).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

async function baixar(base: string, d: DocumentoCliente, v: VersaoDocumento) {
  const a = await api.get<{ nome: string; conteudo_base64: string }>(`${base}/${d.id}/versoes/${v.id}/arquivo`);
  const bytes = Uint8Array.from(atob(a.conteudo_base64), (x) => x.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes as BlobPart]));
  const el = document.createElement('a');
  el.href = url;
  el.download = a.nome;
  el.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type Col = 'nome' | 'tipo' | 'relacionado' | 'versao' | 'atualizado' | 'situacao';

export function DocumentosDoCliente({ clienteId, clienteAtivo, aoMudar }: { clienteId: string; clienteAtivo: boolean; aoMudar?: () => void }) {
  return <PainelDocumentos origem={{ base: `/api/clientes/${clienteId}/documentos`, entidade: 'documento_cliente', lista: 'cliente-documentos', titulo: 'Documentos do Cliente', vazio: 'Nenhum documento enviado para este Cliente.', tituloEnvio: 'Enviar documento do Cliente', teste: 'aba-documentos-cliente', podeCriar: clienteAtivo, motivoBloqueio: 'Cliente inativo', aoMudar }} />;
}

/** P2-33 (D5): aba Documentos da Oportunidade — envio e novas versões também depois do encerramento. */
export function DocumentosDaOportunidade({ oportunidadeId, numero, aoMudar }: { oportunidadeId: string; numero: string; aoMudar?: () => void }) {
  return <PainelDocumentos origem={{ base: `/api/oportunidades/${oportunidadeId}/documentos`, entidade: 'documento_oportunidade', lista: 'oportunidade-documentos', titulo: 'Documentos da oportunidade', vazio: 'Nenhum documento enviado para esta oportunidade.', tituloEnvio: `Anexar documento · Oportunidade #${numero}`, teste: 'aba-documentos-oportunidade', podeCriar: true, aoMudar }} />;
}

function PainelDocumentos({ origem }: { origem: OrigemDocumentos }) {
  const { pode } = useSessao();
  const base = origem.base;
  const carga = useCarga(() => api.get<RespostaDocumentos>(base), [base]);
  const [relacionado, setRelacionado] = useState('');
  const [situacao, setSituacao] = useState<'ativo' | 'inativo' | ''>('ativo');
  const [tipo, setTipo] = useState<TipoDocumento | ''>('');
  const [aberto, setAberto] = useState<string | null>(null);
  const [envio, setEnvio] = useState<{ documento?: DocumentoCliente } | null>(null);
  const [inativar, setInativar] = useState<DocumentoCliente | null>(null);
  const [erro, setErro] = useState('');
  const ord = useOrdenacao<Col>('atualizado', 'desc');
  const pers = usePersonalizacao(origem.lista, { filtros: { situacao, tipo, relacionado }, aplicarFiltros: (e: Record<string, unknown>) => (setSituacao((e.situacao as 'ativo' | 'inativo' | '') ?? 'ativo'), setTipo((e.tipo as string) ?? ''), setRelacionado((e.relacionado as string) ?? '')), ordenacao: ord });
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={() => void carga.recarregar()} teste="documentos-cliente" />;
  const r = carga.dados;
  const podeEnviar = pode('enviar', origem.entidade) && r.armazenamentoConfigurado;
  const comRelacionado = Boolean(r.relacionados);
  const filtrados = r.documentos.filter((d) => (!situacao || d.situacao === situacao) && (!tipo || d.tipo === tipo) && (!relacionado || (relacionado === 'sem' ? !d.relacionado : d.relacionado?.id === relacionado)));
  const lista = ord.ordenar(filtrados, {
    nome: (d) => d.nome,
    tipo: (d) => d.tipoRotulo,
    relacionado: (d) => d.relacionado?.rotulo,
    versao: (d) => d.versoes[0]?.numero ?? 0,
    atualizado: (d) => d.versoes[0]?.enviadoEm ?? d.criadoEm,
    situacao: (d) => d.situacao,
  });
  const conta = (x: string) => r.documentos.filter((d) => d.situacao === x).length;
  const atualizar = (novo: RespostaDocumentos) => (carga.setDados(novo), origem.aoMudar?.());
  return (
    <section data-teste={origem.teste}>
      <div className="titulo-pag-linha">
        <h2 className="sem-margem">{origem.titulo}</h2>
        <Ajuda titulo={origem.titulo}>Arquivos até 20 MB (PDF, imagem, Office ou e-mail). Nova versão mantém a anterior no histórico e disponível; inativar exige justificativa e fica na auditoria; nada é apagado.</Ajuda>
        <span className="espaco" />
        {podeEnviar && (
          <button type="button" className="botao-primario" onClick={() => setEnvio({})} disabled={!origem.podeCriar} title={origem.podeCriar ? undefined : origem.motivoBloqueio} data-teste="novo-documento">
            <Icone nome="mais" /> {comRelacionado ? 'Anexar documento' : 'Enviar documento'}
          </button>
        )}
      </div>
      <BarraFiltros
        teste="documentos"
        direita={pers.controles}
        fixos={
          <>
            <label>
              Status
              <select value={situacao} onChange={(e) => setSituacao(e.target.value as typeof situacao)} data-teste="filtro-situacao-documento">
                <option value="ativo">Ativos ({conta('ativo')})</option>
                <option value="inativo">Inativados ({conta('inativo')})</option>
                <option value="">Todos ({r.documentos.length})</option>
              </select>
            </label>
            <label>
              Tipo
              <select value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)} data-teste="filtro-tipo-documento">
                <option value="">Todos</option>
                {r.tipos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.rotulo}
                  </option>
                ))}
              </select>
            </label>
            {comRelacionado && (
              <label>
                Relacionado a
                <select value={relacionado} onChange={(e) => setRelacionado(e.target.value)} data-teste="filtro-relacionado-documento">
                  <option value="">Todos</option>
                  {r.relacionados!.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.rotulo}
                    </option>
                  ))}
                  <option value="sem">Sem vínculo</option>
                </select>
              </label>
            )}
          </>
        }
        aoLimpar={() => (setSituacao('ativo'), setTipo(''), setRelacionado(''))}
      />
      {!r.armazenamentoConfigurado && <p className="aviso aviso-alerta">O armazenamento de arquivos não está configurado neste ambiente: os documentos não podem ser enviados.</p>}
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      {lista.length === 0 ? (
        <p className="vazio" data-teste="documentos-vazio">
          {r.documentos.length === 0 ? origem.vazio : 'Nenhum documento com estes filtros.'}
        </p>
      ) : (
        <div className="rolagem quadro-tabela primeira-fixa">
          <table ref={pers.tabela} className="tabela tabela-compacta" data-teste="lista-documentos">
            <thead>
              <tr>
                <Th ord={ord} coluna="nome">Documento</Th>
                <Th ord={ord} coluna="tipo">Tipo</Th>
                {comRelacionado && <Th ord={ord} coluna="relacionado">Relacionado a</Th>}
                <th>Arquivo atual</th>
                <Th ord={ord} coluna="versao" numero>Versão</Th>
                <Th ord={ord} coluna="atualizado" padrao="desc">Enviado em</Th>
                <Th ord={ord} coluna="situacao">Status</Th>
                <th className="col-acoes">Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((d) => {
                const atual = d.versoes[0];
                const expandido = aberto === d.id;
                return (
                  <Fragment key={d.id}>
                    <tr className={d.situacao === 'inativo' ? 'linha-historico' : ''} data-teste={`documento-${d.nome}`}>
                      <td>
                        {d.oculto ? (
                          <span className="texto-secundario" data-teste="documento-oculto">🔒 Documento restrito</span>
                        ) : (
                          <button type="button" className="botao-link" aria-expanded={expandido} onClick={() => setAberto(expandido ? null : d.id)} data-teste={`expandir-documento-${d.nome}`}>
                            {expandido ? '▾' : '▸'} {d.nome}
                          </button>
                        )}
                        {d.restrito && !d.oculto && <span className="selo selo-neutro" style={{ marginLeft: 6 }} data-teste={`restrito-${d.nome}`}>🔒 Restrito</span>}
                      </td>
                      <td>{d.tipoRotulo}</td>
                      {comRelacionado && <td data-teste={`relacionado-${d.nome}`}>{d.relacionado?.rotulo ?? '—'}</td>}
                      <td>
                        {atual && (
                          <button type="button" className="botao-link" onClick={() => baixar(base, d, atual).catch((e: Error) => setErro(e.message))} data-teste={`baixar-documento-${d.nome}`}>
                            📎 {atual.nomeArquivo} <span className="texto-secundario">({tamanhoTexto(atual.tamanho)})</span>
                          </button>
                        )}
                      </td>
                      <td className="numero">v{atual?.numero ?? 1}</td>
                      <td className="nowrap">
                        {atual ? dataHoraBr(atual.enviadoEm) : '—'}
                        <div className="texto-secundario">{atual?.enviadoPor}</div>
                      </td>
                      <td>
                        <SeloSituacao texto={d.situacao === 'ativo' ? 'Ativo' : 'Inativado'} tom={d.situacao === 'ativo' ? 'positivo' : 'encerrado'} />
                      </td>
                      <td className="col-acoes nowrap">
                        {d.situacao === 'ativo' && podeEnviar && !d.oculto && (
                          <button type="button" className="botao-secundario pequeno" onClick={() => setEnvio({ documento: d })} data-teste={`nova-versao-${d.nome}`}>
                            Nova versão
                          </button>
                        )}
                        {d.situacao === 'ativo' && pode('inativar', origem.entidade) && (
                          <button type="button" className="botao-secundario pequeno" onClick={() => setInativar(d)} data-teste={`inativar-documento-${d.nome}`}>
                            Inativar 🔒
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandido && (
                      <tr className="linha-detalhe">
                        <td colSpan={comRelacionado ? 8 : 7}>
                          <div data-teste={`historico-documento-${d.nome}`}>
                            <strong>Histórico de versões</strong>
                            <ul className="lista-simples">
                              {d.versoes.map((v) => (
                                <li key={v.id}>
                                  v{v.numero} · {dataHoraBr(v.enviadoEm)} · {v.enviadoPor} ·{' '}
                                  <button type="button" className="botao-link" onClick={() => baixar(base, d, v).catch((e: Error) => setErro(e.message))} data-teste={`baixar-versao-${d.nome}-${v.numero}`}>
                                    {v.nomeArquivo}
                                  </button>{' '}
                                  <span className="texto-secundario">({tamanhoTexto(v.tamanho)})</span>
                                  {v.observacao && <span> — {v.observacao}</span>}
                                </li>
                              ))}
                              {d.situacao === 'inativo' && (
                                <li data-teste={`inativacao-documento-${d.nome}`}>
                                  Inativado em {d.inativadoEm ? dataHoraBr(d.inativadoEm) : '—'} por {d.inativadoPor ?? '—'}: {d.motivoInativacao}
                                </li>
                              )}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="rodape-tabela">{lista.length === 1 ? '1 documento' : `${lista.length} documentos`}</div>
        </div>
      )}
      {envio && <ModalEnviarDocumento base={base} titulo={origem.tituloEnvio} tipos={r.tipos} relacionados={r.relacionados} podeRestrito={Boolean(r.podeRestrito)} limiteMb={r.limiteMb} documento={envio.documento} aoFechar={() => setEnvio(null)} aoSalvar={(n) => (setEnvio(null), atualizar(n))} />}
      {inativar && <ModalInativarDocumento base={base} documento={inativar} aoFechar={() => setInativar(null)} aoSalvar={(n) => (setInativar(null), atualizar(n))} />}
    </section>
  );
}

function ModalEnviarDocumento({ base, titulo, tipos, relacionados, podeRestrito, limiteMb, documento, aoFechar, aoSalvar }: { base: string; titulo: string; tipos: RespostaDocumentos['tipos']; relacionados?: RespostaDocumentos['relacionados']; podeRestrito?: boolean; limiteMb: number; documento?: DocumentoCliente; aoFechar: () => void; aoSalvar: (n: RespostaDocumentos) => void }) {
  const [tipo, setTipo] = useState<TipoDocumento | ''>('');
  const [relacionado, setRelacionado] = useState('');
  const [nome, setNome] = useState('');
  const [observacao, setObservacao] = useState('');
  const [restrito, setRestrito] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [tentou, setTentou] = useState(false);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const grandeDemais = arquivo ? arquivo.size > limiteMb * 1024 * 1024 : false;
  const faltam = [!documento && !tipo && 'Escolha o tipo do documento.', !documento && !nome.trim() && 'Informe o nome do documento.', !arquivo && 'Escolha o arquivo.', grandeDemais && `O arquivo passa do limite de ${limiteMb} MB.`].filter(Boolean) as string[];
  const salvar = async () => {
    setTentou(true);
    if (faltam.length) return;
    setErro('');
    setSalvando(true);
    try {
      const corpo = { observacao, arquivo: await arquivoParaApi(arquivo), ...(documento ? {} : { tipo, nome: nome.trim(), ...(relacionado ? { relacionado } : {}), ...(restrito ? { restrito: true } : {}) }) };
      aoSalvar(await api.post<RespostaDocumentos>(documento ? `${base}/${documento.id}/versoes` : base, corpo));
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={documento ? `Nova versão — ${documento.nome}` : titulo}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" onClick={() => void salvar()} disabled={salvando} data-teste="salvar-documento">
            {salvando ? 'Enviando…' : documento ? 'Enviar nova versão' : 'Enviar'}
          </button>
        </>
      }
    >
      {documento && <p className="aviso aviso-info">A versão v{documento.versoes[0]?.numero ?? 1} continua no histórico e disponível para consulta.</p>}
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <div className="grade-form sem-borda">
        {!documento && (
          <>
            <label>
              Tipo
              <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoDocumento | '')} required data-teste="documento-tipo">
                <option value="">Escolha…</option>
                {tipos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.rotulo}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Nome do documento
              <input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={200} required data-teste="documento-nome" />
            </label>
            {relacionados && (
              <label>
                Relacionado a (opcional)
                <select value={relacionado} onChange={(e) => setRelacionado(e.target.value)} data-teste="documento-relacionado">
                  <option value="">Sem vínculo</option>
                  {relacionados.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.rotulo}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {relacionados && podeRestrito && (
              <label className="opcao largura-total opcao-restrito">
                <input type="checkbox" checked={restrito} onChange={(e) => setRestrito(e.target.checked)} data-teste="documento-restrito" />
                {/* texto e "?" juntos ao lado da caixa (o "?" solto no rótulo empurrava a caixa para uma linha própria) */}
                <span>
                  Documento restrito
                  <Ajuda titulo="Documento restrito">Só quem tem a permissão “Ver documentos restritos” vê o nome e baixa o arquivo. Nunca vai para PDF comercial. Não pode ser mudado depois do envio.</Ajuda>
                </span>
              </label>
            )}
          </>
        )}
        <label className="largura-total">
          Arquivo (até {limiteMb} MB: PDF, imagem, Office ou e-mail)
          <input type="file" accept={ACEITE_DOCUMENTO} onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} data-teste="documento-arquivo" />
        </label>
        <label className="largura-total">
          Observação (opcional)
          <input value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={1000} data-teste="documento-observacao" />
        </label>
      </div>
      {tentou && faltam.length > 0 && (
        <ul className="aviso aviso-erro lista-simples" role="alert" data-teste="documento-faltam">
          {faltam.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function ModalInativarDocumento({ base, documento: d, aoFechar, aoSalvar }: { base: string; documento: DocumentoCliente; aoFechar: () => void; aoSalvar: (n: RespostaDocumentos) => void }) {
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const curta = justificativa.trim().length < 4;
  const salvar = async () => {
    if (curta) return setErro('Informe a justificativa (mínimo de 4 caracteres).');
    setErro('');
    setSalvando(true);
    try {
      aoSalvar(await api.post<RespostaDocumentos>(`${base}/${d.id}/inativar`, { justificativa: justificativa.trim() }));
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={`Inativar documento — ${d.nome}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" onClick={() => void salvar()} disabled={salvando} data-teste="confirmar-inativar-documento">
            Inativar
          </button>
        </>
      }
    >
      <p className="aviso aviso-info">O documento e todas as versões continuam guardados e consultáveis (nada é apagado). A inativação fica na auditoria com a justificativa.</p>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <label className="campo largura-total">
        Justificativa
        <textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} maxLength={1000} rows={3} data-teste="justificativa-inativar-documento" />
      </label>
    </Modal>
  );
}
